import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';

import './App.css';
import Header from './Header.tsx';
import Footer from './Footer.tsx';
import HomiePage from './pages/HomiePage.tsx';
import PrivacyPolicy from './PrivacyPolicy.tsx';
import HIPAA from './HIPAA.tsx';
import TermsOfUse from './TermsOfUse.tsx';
import LegalDisclaimers from './LegalDisclaimers.tsx';
import RefundPolicy from './RefundPolicy.tsx';
import Accessibility from './Accessibility.tsx';
import Learn from './Learn.tsx';
import About from './About.tsx';
import Stores from './Stores.tsx';
import Franchising from './franchise/Franchise.tsx';
import CtCannabisInfo from './CannabisSafetyInformation.tsx';
import Sustainability from './Sustainability.tsx';
import LoyaltySignup from './LoyaltySignup.tsx';
import AgeGate from './AgeGate.tsx';
import { StackAdapt } from './StackAdapt.tsx';
import MenuPage from './MenuPage.tsx';
import OrderConfirmation from './OrderConfirmation.tsx';
import { CartProvider } from './contexts/CartContext.tsx';
import { ProductsProvider } from './contexts/ProductsContext.tsx';
import AdminPortal from './components/AdminPortal.tsx';
import AdminPortalRedesign from './components/AdminPortalRedesign.tsx';
import EmployeeSurvey from './components/EmployeeSurvey.tsx';
import SurveyResultsPage from './components/SurveyResultsPage.tsx';
import analyticsService from './services/AnalyticsService.ts';
import FrustrationDetectionService from './services/FrustrationDetectionService.ts';

// Wrapper to force MenuPage remounting on route changes
function MenuPageWrapper() {
  const location = useLocation();
  return <MenuPage key={location.pathname} />;
}

function AppContent() {
  // Lazy init so already-verified visitors never see the overlay flash on load
  const [isAgeVerified, setIsAgeVerified] = useState(
    () => sessionStorage.getItem('ageGatePassed') === 'true'
  );
  const location = useLocation();

  useEffect(() => {
    // Clean up old localStorage key from previous implementation
    localStorage.removeItem('birthdayCollected');
  }, []);

  // Initialize frustration detection service
  useEffect(() => {
    if (isAgeVerified) {
      // Initialize frustration detection (singleton pattern ensures it only happens once)
      FrustrationDetectionService.getInstance();
    }
  }, [isAgeVerified]);

  // Track page views and session changes on route changes
  useEffect(() => {
    if (isAgeVerified) {
      // Call onPageChange for session tracking
      analyticsService.onPageChange();
      
      // Track the page view
      analyticsService.trackPageView({
        page_title: document.title,
        page_path: location.pathname,
        referrer: document.referrer || 'direct'
      });
    }
  }, [location.pathname, isAgeVerified]);

  // Bypass age gate for admin portal and the internal employee survey
  const pathLower = location.pathname.toLowerCase();
  const isSurveyPage = pathLower === '/employeeengagementsurvey2026';
  const isSurveyResultsPage = pathLower === '/employeeengagementsurvey2026/results';
  const isAgeGateBypassRoute = pathLower === '/admin' || isSurveyPage || isSurveyResultsPage;
  // The gate renders as an overlay ON TOP of the page (never instead of it), so the
  // real page content, headings, and meta tags always exist for crawlers
  const showAgeGate = !isAgeVerified && !isAgeGateBypassRoute;

  // Lock page scroll behind the overlay while it's up
  useEffect(() => {
    if (showAgeGate) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [showAgeGate]);

  const isMenuPage = pathLower.startsWith('/menu/') ||
                     pathLower.includes('/dispensary-medical-menu') ||
                     pathLower.includes('/dispensary-recreational-menu');
  const isAdminPage = pathLower === '/admin';
  const isStandaloneSurveyPage = isSurveyPage || isSurveyResultsPage;

  return (
    <ProductsProvider>
    <CartProvider>
      {/* Third-party pixel: keep it off until the visitor is verified (or on a bypass route),
          matching the pre-overlay behavior where nothing loaded behind the gate */}
      {!showAgeGate && <StackAdapt />}
      <div className={`App ${isAdminPage ? 'admin-mode' : ''}`} style={{ display: 'flex', flexDirection: 'column' }}>
        {!isAdminPage && !isStandaloneSurveyPage && (
          <Header />
        )}
        
        <div className="main-content">
          <Routes>
            <Route path="/" element={<HomiePage />} />
            
            {/* New dynamic store routes */}
            <Route path="/store" element={<Stores />} />
            <Route path="/store/:state" element={<Stores />} />
            <Route path="/store/:state/:storeName" element={<Stores />} />
            <Route path="/store/:state/:storeName/dispensary-medical-menu" element={<MenuPageWrapper />} />
            <Route path="/store/:state/:storeName/dispensary-recreational-menu" element={<MenuPageWrapper />} />
            
            {/* Store redirects for legacy URLS */}
            <Route path="/store/ct001" element={<Navigate to="/store/connecticut/west-hartford" replace />} />
            <Route path="/store/ct002" element={<Navigate to="/store/connecticut/stamford" replace />} />
            <Route path="/store/md001" element={<Navigate to="/store/maryland/olney" replace />} />
            <Route path="/store/nj001" element={<Navigate to="/store/new-jersey/voorhees" replace />} />
            <Route path="/store/ri001" element={<Navigate to="/store/rhode-island/exeter" replace />} />
            <Route path="/store/vt001" element={<Navigate to="/store/vermont/essex-junction" replace />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/hipaa" element={<HIPAA />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/legal-disclaimers" element={<LegalDisclaimers />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/CtCannabisInfo" element={<CtCannabisInfo />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/franchising/*" element={<Franchising />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/about" element={<About />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/menu/:menuId" element={<MenuPage />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/new-dispensary" element={<LoyaltySignup />} />
            <Route path="/early-signup" element={<LoyaltySignup />} />

            <Route path="/EmployeeEngagementSurvey2026" element={<EmployeeSurvey />} />
            <Route path="/EmployeeEngagementSurvey2026/results" element={<SurveyResultsPage />} />

            {/* Add this catch-all route at the end for bad or unsupported links */}
            <Route path="/admin" element={<AdminPortalRedesign />} />
            <Route path="/admin-old" element={<AdminPortal />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>

        {!isMenuPage && !isAdminPage && !isStandaloneSurveyPage && <Footer />}

        {showAgeGate && <AgeGate onVerified={() => setIsAgeVerified(true)} />}
      </div>
    </CartProvider>
    </ProductsProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;